import React, { useState, useEffect } from "react";
import "../Css/CustomerHistory.css";
import Header from "../Header";
import { ArrowLeft, CheckCircle2, AlertCircle, Clock, Car } from "lucide-react";
import { Button } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import {
  handleError,
  goBack,
  formatBookingId,
  formatDateStringdot,
  mondayDayYearFormat,
} from "../../CommonJquery/CommonJquery";
import {
  server_post_data,
  driver_all_booking_report,
} from "../../ServiceConnection/serviceconnection.js";
import Breadcrumb from "../../CommonJquery/Breadcrumb.js";
import Loader from "../Loader.js";

const DriverAllReports = () => {
  const { id } = useParams(); // Get primary_id from URL params
  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [driverData, setDriverData] = useState([]); // State for all booking data
  const [filteredData, setFilteredData] = useState([]); // State for search/status filtered data
  const [customerData, setCustomerData] = useState({}); // State for driver details

  const itemsPerPage = 10; // Number of items per page

  useEffect(() => {
    master_data_get();
  }, []);

  const master_data_get = async () => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    // Send primary_id instead of user_id, assuming API supports it
    fd.append("user_id", id);
    try {
      const response = await server_post_data(driver_all_booking_report, fd);
      // //console.log("API Response:", JSON.stringify(response.data, null, 2)); // Log full response
      if (response.data.error) {
        handleError(response.data.message);
      } else {
        // Extract the booking data correctly
        const bookingData = response.data.message.booking_data || [];
        //console.log("Booking data:", bookingData);

        // Set driver data
        setDriverData(bookingData);
        // Also set filtered data initially to show all records
        setFilteredData(bookingData);
        // Extract customer data from the first booking if available
        if (bookingData.length > 0) {
          const driverInfo = {
            name:
              bookingData[0].name ||
              `${bookingData[0].first_name || ""} ${
                bookingData[0].last_name || ""
              }`,
            mobile_no: bookingData[0].mobile_no,
            email: bookingData[0].email,
            driving_licence: bookingData[0].driving_licence,
            status:
              bookingData[0].step_for_booking == 4 ? "Active" : "Inactive",
          };
          setCustomerData(driverInfo);
        }
      }
    } catch (error) {
      console.error("API Error:", error);
      handleError("network");
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page

    // Apply both search and status filter
    applyFilters(searchValue, statusFilter);
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page

    // Apply both search and status filter
    applyFilters(searchTerm, status);
  };

  // Combined filter function to handle both search and status filters
  const applyFilters = (search, status) => {
    let filtered = [...driverData];

    // Apply search filter if there's a search term
    if (search) {
      filtered = filtered.filter(
        (row) =>
          Object.values(row).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(search.toLowerCase())
          ) ||
          // Search in car_data fields
          (row.car_data &&
            row.car_data[0] &&
            (row.car_data[0].car_name
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
              row.car_data[0].car_tagline
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              row.car_data[0].car_no
                ?.toLowerCase()
                .includes(search.toLowerCase())))
      );
    }

    // Apply status filter if there's a status selected

    if (status) {
      filtered = filtered.filter((row) => {
        // Determine booking status based on flag value
        // console.log(row.step_for_booking);
        const bookingStatus = row.step_for_booking == 4 ? "Active" : "Inactive";
        return bookingStatus.toLowerCase() === status.toLowerCase();
      });
    }

    //console.log("Filtered Data:", filtered); // Log filtered data
    setFilteredData(filtered);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Active":
        return "cust_hist_status_active";
      case "InActive":
        return "cust_hist_status_completed"; // Reusing repo class for Inactive
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    return status || "Unknown";
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  console.log("Pagination Info:", {
    currentPage,
    indexOfFirstItem,
    indexOfLastItem,
    totalPages,
    currentItemsLength: currentItems.length,
  }); // Log pagination details

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    buttons.push(
      <li className="cust_hist_page_item" key="prev">
        <button
          className="cust_hist_page_link cust_hist_page_prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <span className="sr-only">Previous</span>
          <svg
            className="cust_hist_page_icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </li>
    );

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <li className="cust_hist_page_item" key={i}>
          <button
            className={`cust_hist_page_link ${
              currentPage === i ? "cust_hist_page_active" : ""
            }`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    buttons.push(
      <li className="cust_hist_page_item" key="next">
        <button
          className="cust_hist_page_link cust_hist_page_next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          <span className="sr-only">Next</span>
          <svg
            className="cust_hist_page_icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </li>
    );

    return buttons;
  };

  // Calculate total dues for a booking
  const calculateTotalDue = (booking) => {
    return (
      parseFloat(booking.due_rent || 0) +
      parseFloat(booking.due_tolls || 0) +
      parseFloat(booking.due_damage || 0) +
      parseFloat(booking.due_ticket || 0) +
      parseFloat(booking.due_other_charges || 0)
    );
  };

  const getStatusBadge = (booking) => {
    let dayweek = "Day";
    if (booking.payment_day_week === 2) {
      dayweek = "Week";
    }
    ///payment_cycle_switch
    const { step_for_booking, fill_aggriment_status, payment_cycle_switch } =
      booking;
    console.log(
      step_for_booking,
      fill_aggriment_status,
      payment_cycle_switch,
      dayweek
    );
    switch (step_for_booking) {
      case "0":
        return (
          <div className="status-badge status-new">
            <Clock size={16} className="status-icon" />
            <span>New</span>
          </div>
        );
      case "1":
        return (
          <div className="status-badge status-inprogress">
            <Clock size={16} className="status-icon" />
            <span>InProgress</span>
          </div>
        );
      case "2":
        return fill_aggriment_status == "0" ? (
          <div className="status-badge status-not-signed">
            <AlertCircle size={16} className="status-icon" />
            <span>Agreement not signed</span>
          </div>
        ) : (
          <div className="status-badge status-signed">
            <CheckCircle2 size={16} className="status-icon" />
            <span>Agreement signed</span>
          </div>
        );
      case "3":
        return (
          <div className="status-badge status-ready">
            <CheckCircle2 size={16} className="status-icon" />
            <span>Ready for Pickup</span>
          </div>
        );
      case "4":
        return (
          <>
            <div className="status-badge status-ongoing mb-0">
              <Car size={16} className="status-icon" />
              <span>OnGoing</span>
            </div>
            <div className="status-badge mb-0">
              <span>
                <span
                  style={{
                    fontWeight: "500",
                    color: "var(--medium-text)",
                  }}
                >
                  Payment Cycle:
                </span>{" "}
                {dayweek}
              </span>
            </div>
          </>
        );
      case "5":
        return payment_cycle_switch == "1" ? (
          <>
            <div className="status-badge status-rejected mb-0">
              <CheckCircle2 size={16} className="status-icon" />
              <span>Payment Cycle Switch</span>
            </div>
            <div className="status-badge mb-0">
              <span>
                <span
                  style={{
                    fontWeight: "500",
                    color: "var(--medium-text)",
                  }}
                >
                  Payment Cycle:
                </span>{" "}
                {dayweek}
              </span>
            </div>
          </>
        ) : payment_cycle_switch === "2" ? (
          <>
            <div className="status-badge status-rejected mb-0">
              <CheckCircle2 size={16} className="status-icon" />
              <span>Ipass Switch</span>
            </div>
            <div className="status-badge mb-0">
              <span>
                <span
                  style={{
                    fontWeight: "500",
                    color: "var(--medium-text)",
                  }}
                >
                  Payment Cycle:
                </span>{" "}
                {dayweek}
              </span>
            </div>
          </>
        ) : payment_cycle_switch === "3" ? (
          <>
            <div className="status-badge status-rejected mb-0">
              <CheckCircle2 size={16} className="status-icon" />
              <span>Car Switch</span>
            </div>
            <div className="status-badge mb-0">
              <span>
                <span
                  style={{
                    fontWeight: "500",
                    color: "var(--medium-text)",
                  }}
                >
                  Payment Cycle:
                </span>{" "}
                {dayweek}
              </span>
            </div>
          </>
        ) : payment_cycle_switch === "4" ? (
          <>
            <div className="status-badge status-rejected mb-0">
              <CheckCircle2 size={16} className="status-icon" />
              <span>Drop After Repo</span>
            </div>
            <div className="status-badge mb-0">
              <span>
                <span
                  style={{
                    fontWeight: "500",
                    color: "var(--medium-text)",
                  }}
                >
                  Payment Cycle:
                </span>{" "}
                {dayweek}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="status-badge status-completed mb-0">
              <CheckCircle2 size={16} className="status-icon" />
              <span>Completed</span>
            </div>
            <div className="status-badge mb-0">
              <span>
                <span
                  style={{
                    fontWeight: "500",
                    color: "var(--medium-text)",
                  }}
                >
                  Payment Cycle:
                </span>{" "}
                {dayweek}
              </span>
            </div>
          </>
        );
      case "6":
        return (
          <div className="status-badge status-ongoing">
            <CheckCircle2 size={16} className="status-icon" />
            <span>Car Switch Ongoing</span>
          </div>
        );
      case "7":
        return (
          <div className="status-badge status-rejected">
            <CheckCircle2 size={16} className="status-icon" />
            <span>Repossessed</span>
          </div>
        );
      case "9":
        return (
          <div className="status-badge status-rejected">
            <AlertCircle size={16} className="status-icon" />
            <span>Cancelled</span>
          </div>
        );
      case "10":
        return (
          <div className="status-badge status-rejected">
            <AlertCircle size={16} className="status-icon" />
            <span>Rejected</span>
          </div>
        );
      case "11":
        return (
          <div className="status-badge status-rejected">
            <AlertCircle size={16} className="status-icon" />
            <span>Canceled</span>
          </div>
        );
      default:
        return (
          <div className="status-badge">
            <Clock size={16} className="status-icon" />
            <span>Unknown</span>
          </div>
        );
    }
  };

  function formatTimeTo24Hour(timeString) {
    if (!timeString) return "-";

    const [hourStr, minuteStr] = timeString.split(":");
    return `${hourStr}:${minuteStr}`;
  }

  return (
    <>
      {/* {showLoaderAdmin && <div>Loading...</div>}{" "} */}
      {showLoaderAdmin && <Loader />}
      {/* Replace with Loader component if available */}
      <Header />
      <div className="pageCntn_head">
        <div className="pageNameDiv">
          {/* <p>Driver All Report</p>
          <img src={GreyArrow} alt="Rapid Car Leasing" /> */}
          <Breadcrumb
            items={[
              { label: "All Drivers Report", path: "/payment-reports" },
              { label: "Driver All Reports", path: "/driver-all-reports" },
            ]}
          />
        </div>
        <Button
          onClick={goBack}
          startIcon={<ArrowLeft size={17} style={{ marginBottom: "2px" }} />}
          className="Back_bttn_page"
        >
          Back
        </Button>
      </div>
      <div className="cust_hist_container">
        {/* Driver Details Card */}
        <div
          className="cust_hist_card mb-3"
          style={{ position: "sticky", top: 84, zIndex: 19 }}
        >
          <div className="cust_hist_card_body">
            <div className="row">
              <div className="col-md-3">
                <div className="cust_hist_info_box">
                  <p className="cust_hist_info_label">Driver Name</p>
                  <p className="cust_hist_info_value">
                    {customerData.name || "N/A"}
                  </p>
                </div>
              </div>
              <div className="col-md-3">
                <div className="cust_hist_info_box">
                  <p className="cust_hist_info_label">Contact Details</p>
                  <p className="cust_hist_info_value">
                    {customerData.mobile_no || "N/A"}
                  </p>
                  <p className="cust_hist_info_email">
                    {customerData.email || "N/A"}
                  </p>
                </div>
              </div>
              <div className="col-md-3">
                <div className="cust_hist_info_box">
                  <p className="cust_hist_info_label">Driving License No.</p>
                  <p className="cust_hist_info_value">
                    {customerData.driving_licence || "N/A"}
                  </p>
                </div>
              </div>
              <div className="col-md-3">
                <div className="cust_hist_info_box">
                  <p className="cust_hist_info_label">Status</p>
                  <div className="mt-1">
                    <span
                      className={`cust_hist_status_badge ${getStatusClass(
                        customerData.status
                      )}`}
                    >
                      {customerData.status || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking History Card */}
        <div className="cust_hist_card">
          <div className="cust_hist_card_header">
            <div className="cust_hist_header_container">
              <h3 className="cust_hist_title">All Reports</h3>
              <div className="search-filter">
                <div className="cust_hist_search_container">
                  <svg
                    className="cust_hist_search_icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <input
                    type="text"
                    className="cust_hist_search_input"
                    placeholder="Search bookings..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
                <select
                  className="cust_hist_select"
                  value={statusFilter}
                  onChange={handleStatusChange}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="reservationHistoryTable">
            <div
              className="historyTableContainer stickyTableheigh274"
              style={{ overflowX: "auto" }}
            >
              <table className="table margin_class_table">
                <thead className="stickyHeaderClss">
                  <tr>
                    <th scope="col">Booking ID</th>
                    <th scope="col">Car Details</th>
                    <th scope="col">Status</th>
                    <th scope="col">Dates</th>
                    <th scope="col">Balance/Dues</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((option, index) => {
                      //console.log("Rendering Item:", option); // Log each item
                      const carData =
                        option.car_data && option.car_data[0]
                          ? option.car_data[0]
                          : {};
                      // Determine status from available data
                      const bookingStatus =
                        option.flag === "c" ? "Active" : "Inactive";
                      const totalDue = calculateTotalDue(option);

                      return (
                        <tr className="historyTableRow" key={index}>
                          <td>
                            <div
                              style={{
                                marginLeft: "0.5rem",
                                fontSize: "14px",
                                marginTop: "1.2rem",
                                fontWeight: "500",
                                color: "var(--medium-text)",
                              }}
                            >
                              #{formatBookingId(option.primary_id)}
                            </div>
                          </td>
                          <td>
                            <div className="topMargin historyTime">
                              <div className="historyTimeImgText align-items-start">
                                <p>
                                  <span style={{ fontWeight: "700" }}>
                                    {carData.car_name || "N/A"}
                                  </span>
                                  <p>{carData.car_tagline || "N/A"}</p>
                                  <p>{carData.car_no || "N/A"}</p>
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="historyTableRowText historyStatus">
                              <div className="historyTableRowText historyStatus">
                                <button
                                  type="button"
                                  className={`markVip Mark_Vip m-0`}
                                  style={{
                                    width: "fit-content",
                                    backgroundColor: "transparent",
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  {getStatusBadge(option)}
                                </button>
                              </div>
                            </div>
                          </td>
                          <td>
                            <td>
                              <div
                                className="historyTableRowText historyPerson"
                                style={{ flexWrap: "wrap" }}
                              >
                                <div>
                                  <p>
                                    Rent Generated Upto:{" "}
                                    {option?.last_link_date == null ? (
                                      "Not Generated"
                                    ) : (
                                      <p>
                                        {formatDateStringdot(
                                          option.last_link_date
                                        )}
                                      </p>
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <p>
                                    PickUp Date:{" "}
                                    {option.step_for_booking < "4" ? (
                                      "-"
                                    ) : (
                                      <>
                                        {mondayDayYearFormat(option.from_date)}
                                      </>
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <p>
                                    PickUp Time:{" "}
                                    {formatTimeTo24Hour(option.new_from_time)}
                                  </p>
                                </div>
                              </div>
                            </td>
                          </td>
                          <td>
                            <div
                              className="historyTableRowText historyPerson"
                              style={{ flexWrap: "wrap" }}
                            >
                              <p>
                                <strong style={{ color: "green" }}>
                                  Total Paid:
                                </strong>{" "}
                                ${option?.total_paid || 0}
                              </p>
                              <p>
                                <strong style={{ color: "red" }}>
                                  {" "}
                                  {option?.step_for_booking == "5"
                                    ? "Previous Due"
                                    : "Due"}
                                </strong>{" "}
                                ${option?.total_due || 0}
                              </p>
                              <p>
                                <strong style={{ color: "orange" }}>
                                  Advance:
                                </strong>{" "}
                                ${option?.balance_advance || 0}
                              </p>
                            </div>
                          </td>
                          <td>
                            <div className="historyActionsBtns flex-row justify-content-start mb-2">
                              <div className="Action_booking_btn">
                                <Link
                                  to="/WeeklyReport"
                                  state={{ option: option }}
                                >
                                  <button
                                    className="instructions Add_Instructions"
                                    style={{
                                      cursor: "pointer",
                                      paddingRight: "10px",
                                    }}
                                  >
                                    View Report
                                  </button>
                                </Link>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text_align_center">
                        No Results Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="cust_hist_card_footer">
                <div className="cust_hist_pagination_info">
                  Showing{" "}
                  <span className="cust_hist_pagination_bold">
                    {filteredData.length > 0 ? indexOfFirstItem + 1 : 0}
                  </span>{" "}
                  to{" "}
                  <span className="cust_hist_pagination_bold">
                    {Math.min(indexOfLastItem, filteredData.length)}
                  </span>{" "}
                  of{" "}
                  <span className="cust_hist_pagination_bold">
                    {filteredData.length}
                  </span>{" "}
                  rentals
                </div>
                <div className="cust_hist_pagination">
                  <nav aria-label="Pagination">
                    <ul className="cust_hist_pagination_list">
                      {renderPaginationButtons()}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DriverAllReports;
